import React, { useState } from 'react';
import '../../styles/components/RegistrationForm.css';
import { FaArrowRight } from 'react-icons/fa';
import CountrySelector, { NationalitySelector } from '../../data/CountrySelector';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';

const findToken = (obj) => {
  if (!obj || typeof obj !== 'object') return null;

  if (obj.token && typeof obj.token === 'string') return obj.token;
  if (obj.auth_token && typeof obj.auth_token === 'string') return obj.auth_token;
  if (obj.access_token && typeof obj.access_token === 'string') return obj.access_token;
  if (obj.accessToken && typeof obj.accessToken === 'string') return obj.accessToken;

  if (obj.success) {
    if (typeof obj.success === 'string') return obj.success;
    if (typeof obj.success === 'object') {
      const nestedToken = findToken(obj.success);
      if (nestedToken) return nestedToken;
    }
  }

  if (obj.data && typeof obj.data === 'object') {
    const nestedToken = findToken(obj.data);
    if (nestedToken) return nestedToken;
  }

  if (obj.Profile && typeof obj.Profile === 'object') {
    const nestedToken = findToken(obj.Profile);
    if (nestedToken) return nestedToken;
  }

  for (const key of Object.keys(obj)) {
    if (obj[key] && typeof obj[key] === 'object') {
      const nestedToken = findToken(obj[key]);
      if (nestedToken) return nestedToken;
    }
  }
  return null;
};

const RegistrationForm = ({ t, onVerificationRequired, onForgotPassword }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    sector: '',
    firstName: '',
    lastName: '',
    email: '',
    country: '',
    countryCode: '',
    nationality: '',
    phone: '',
    occupation: '',
    companyName: '',
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isEmailExists, setIsEmailExists] = useState(false);

  const REGISTER_URL = `${API_CONFIG.BASE_URL}/register`;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const numbersOnly = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Country Selector - تحديث الدولة وكود الهاتف والجنسية
  const handleCustomCountryChange = (e) => {
    setFormData(prev => ({
      ...prev,
      country: e.target.value,
      countryCode: e.dialCode,
      nationality: e.nationality || prev.nationality
    }));

    if (formErrors['country']) {
      setFormErrors(prev => ({ ...prev, country: '' }));
    }
  };

  //  Nationality Selector - تحديث الجنسية فقط
  const handleNationalityChange = (e) => {
    setFormData(prev => ({ ...prev, nationality: e.target.value }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title) errors.title = 'Title is required';
    if (!formData.sector) errors.sector = 'Sector is required';
    if (!formData.firstName) errors.firstName = 'First name is required';
    if (!formData.lastName) errors.lastName = 'Last name is required';
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.country) errors.country = 'Country is required';
    if (!formData.phone) errors.phone = 'Phone is required';
    if (!formData.occupation) errors.occupation = 'Position is required';
    if (!formData.companyName) errors.companyName = 'Company name is required';

    return errors;
  };

  const isPasswordLongEnough = formData.password.length >= 6;
  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const showPasswordMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  const isFormValid =
    formData.title &&
    formData.sector &&
    formData.firstName &&
    formData.lastName &&
    formData.email &&
    formData.country &&
    formData.phone &&
    formData.occupation &&
    formData.companyName &&
    isPasswordLongEnough &&
    passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    if (!isFormValid) return;

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    const submissionData = {
      title: formData.title,
      sector: formData.sector,
      name: formData.firstName,
      lname: formData.lastName,
      email: formData.email,
      country: formData.country,
      nationality: formData.nationality,
      contact: `${formData.countryCode}${formData.phone}`,
      position: formData.occupation,
      company: formData.companyName,
      password: formData.password,
      c_password: formData.confirmPassword,
      source: 'registration_form'
    };

    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(REGISTER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();
      console.log("Full Server Response:", data);

      const hasEmailTakenError = data?.error?.email && (
        (Array.isArray(data.error.email) && data.error.email.some(msg => msg.includes("already been taken"))) ||
        (typeof data.error.email === 'string' && data.error.email.includes("already been taken"))
      );

      if (hasEmailTakenError) {
        setIsEmailExists(true);
        setIsSubmitting(false);
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      if (onVerificationRequired) {
        const token = findToken(data);
        if (token) localStorage.setItem('auth_token', token);

        // Force unverified flags in the stored response to ensure persistence on reload
        const storedData = {
          ...data,
          userVerified: false,
          user_verified: false
        };
        localStorage.setItem('login_response_full', JSON.stringify(storedData));
        localStorage.setItem('unverified_email', formData.email);

        onVerificationRequired({
          token: token,
          email: formData.email,
          response: storedData,
          from: 'register'
        });
        navigate('/verify-email');
      }

    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.message || 'An error occurred during registration');
      setShowErrorModal(true);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-column register-column">
      <h2 className="register-title">
        {t('registration_email', 'Registration By Email')}
      </h2>
      <p className="register-description">
        {t('fill_form', 'Please Fill The Below Form To Proceed With Your Subscription')}
      </p>

      {submitSuccess && (
        <div className="register-alert success">
          <span className="register-alert-icon">✓</span>
          {submitSuccess}
        </div>
      )}
      {submitError && (
        <div className="register-alert error">
          <span className="register-alert-icon">✕</span>
          {submitError}
        </div>
      )}

      <form className="register-form" onSubmit={handleSubmit}>
        {/* Title + Sector */}
        <div className="register-form-row">
          <div className="register-input-group">
            <select
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`register-placeholder-select ${formErrors.title ? 'error' : ''}`}
            >
              <option value="" disabled hidden>{t('select_title', 'Select Title')}</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>
            </select>
            {formErrors.title && <span className="error-message">{formErrors.title}</span>}
          </div>

          <div className="register-input-group">
            <select
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              className={`register-placeholder-select ${formErrors.sector ? 'error' : ''}`}
            >
              <option value="" disabled hidden>{t('select_sector', 'Select Sector')}</option>
              <option value="Government">Government</option>
              <option value="Non-Profit Organization/Association">Non-Profit Organization/Association</option>
              <option value="Private">Private</option>
              <option value="Academic">Academic</option>
              <option value="Individual">Individual</option>
            </select>
            {formErrors.sector && <span className="error-message">{formErrors.sector}</span>}
          </div>
        </div>

        {/* First Name + Last Name */}
        <div className="register-form-row">
          <div className="register-input-group">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t('first_name', 'First Name')}
              className={formErrors.firstName ? 'error' : ''}
            />
            <label>{t('first_name', 'First Name')}</label>
            {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
          </div>

          <div className="register-input-group">
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t('last_name', 'Last Name')}
              className={formErrors.lastName ? 'error' : ''}
            />
            <label>{t('last_name', 'Last Name')}</label>
            {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
          </div>
        </div>

        {/* Email */}
        <div className="register-form-row register-full-width">
          <div className="register-input-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('email_address', 'Email Address')}
              className={formErrors.email ? 'error' : ''}
            />
            <label>{t('email_address', 'Email Address')}</label>
            {formErrors.email && <span className="error-message">{formErrors.email}</span>}
          </div>
        </div>

        {/* ✅ Country + Nationality */}
        <div className="register-form-row">
          <div className="register-input-group country-group">
            <CountrySelector
              value={formData.country}
              onChange={handleCustomCountryChange}
              placeholder={t('select_country', 'Select Country')}
              className={formErrors.country ? 'error' : ''}
              t={t}
            />
            <label>{t('country', 'Country')}</label>
            {formErrors.country && <span className="error-message">{formErrors.country}</span>}
          </div>

          <div className="register-input-group country-group">
            <NationalitySelector
              value={formData.nationality}
              onChange={handleNationalityChange}
              placeholder={t('select_nationality', 'Select Nationality')}
              className={formErrors.nationality ? 'error' : ''}
              t={t}
            />
            <label>{t('nationality', 'Nationality')}</label>
          </div>
        </div>

        {/* Phone */}
        <div className="register-form-row">
          <div className="register-input-group phone-group">
            <div className={`phone-input-wrapper ${formErrors.phone ? 'wrapper-error' : ''}`}>
              <div className="phone-country-code-box">
                {formData.countryCode || '--'}
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('phone', 'Phone')}
                className="phone-embedded-input"
              />
            </div>
            <label>{t('phone', 'Phone')}</label>
            {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
          </div>
        </div>

        {/* Position + Company */}
        <div className="register-form-row">
          <div className='register-input-group'>
            <input
              type="text"
              name="occupation"
              value={formData.occupation}
              onChange={handleChange}
              placeholder={t('position_occupation', 'Position / Occupation')}
              className={formErrors.occupation ? 'error' : ''}
            />
            <label>{t('position_occupation', 'Position / Occupation')}</label>
            {formErrors.occupation && <span className="error-message">{formErrors.occupation}</span>}
          </div>

          <div className='register-input-group'>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder={t('company_name', 'Company Name')}
              className={formErrors.companyName ? 'error' : ''}
            />
            <label>{t('company_name', 'Company Name')}</label>
            {formErrors.companyName && <span className="error-message">{formErrors.companyName}</span>}
          </div>
        </div>

        <hr className="register-form-divider" />

        {/* Password Section */}
        <div className="register-password-section">
          <h3 className="register-section-title">{t('create_password', 'Create Password')}</h3>
          <p className="register-section-subtitle">{t('min_characters', 'minimum 6 characters')}</p>

          <div className="register-form-row">
            <div className="register-input-group">
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('password', 'Password')}
              />
              <label>{t('password', 'Password')}</label>
              {formData.password && !isPasswordLongEnough && (
                <p className="register-validation-message error">
                  {t('password_min_validation', 'Password must be at least 6 characters')}
                </p>
              )}
            </div>

            <div className="register-input-group">
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('confirm_password', 'Confirm Password')}
              />
              <label>{t('confirm_password', 'Confirm Password')}</label>
              {showPasswordMismatch && (
                <p className="register-validation-message error">
                  {t('password_not_match', 'Passwords do not match')}
                </p>
              )}
              {passwordsMatch && (
                <p className="register-validation-message success">
                  {t('password_match', 'Passwords match successfully')}
                </p>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="register-btn-next"
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? (
            <span className="register-spinner"></span>
          ) : (
            <>
              {t('next', 'Next')}
              <FaArrowRight
                style={{ fontSize: "15px" }}
                stroke="currentColor"
                strokeWidth="35"
              />
            </>
          )}
        </button>
      </form>
      {showErrorModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center',
            border: '1px solid #fee2e2',
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <span className="material-icons" style={{ fontSize: '32px' }}>error_outline</span>
            </div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '10px'
            }}>
              {t('registerErrorTitle', 'Registration Error')}
            </h3>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              {submitError}
            </p>
            <button
              type="button"
              onClick={() => setShowErrorModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              {t('close', 'Close')}
            </button>
          </div>
        </div>
      )}

      {isEmailExists && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '40px 32px',
            maxWidth: '460px',
            width: '90%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            border: '1px solid #e2e8f0',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Soft amber top stripe */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '6px',
              backgroundColor: '#f59e0b'
            }} />

            {/* Email Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#fef3c7',
              color: '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 16px rgba(245, 158, 11, 0.1)'
            }}>
              <span className="material-icons" style={{ fontSize: '36px' }}>contact_mail</span>
            </div>

            {/* Title */}
            <h3 style={{
              fontSize: '1.4rem',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '12px',
              fontFamily: 'system-ui, sans-serif'
            }}>
              {t('emailExistsTitle', 'Email Already Exists')}
            </h3>

            {/* Description */}
            <p style={{
              fontSize: '0.975rem',
              color: '#475569',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              {t('emailExistsDesc', 'This email address is already registered in our system. You can choose to change the email address or reset your password if you forgot it.')}
            </p>

            {/* Actions Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                type="button"
                onClick={() => {
                  setIsEmailExists(false);
                  if (onForgotPassword) onForgotPassword();
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#185fa5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 4px 12px rgba(24, 95, 165, 0.15)'
                }}
              >
                {t('resetPasswordBtn', 'Reset Password')}
              </button>

              <button
                type="button"
                onClick={() => setIsEmailExists(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                {t('changeEmailBtn', 'Change Email')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;